import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { config, getAreaBySlug } from "@/config/eventConfig";
import { useSession } from "@/hooks/useSession";
import { usePersistentState } from "@/hooks/usePersistentState";
import { isAreaDismissed, dismissArea } from "@/lib/sessionGates";
import { useRightNow } from "@/context/RightNowContext";
import { SegmentGuide } from "@/components/SegmentGuide";
import { StepEstimate } from "@/components/StepEstimate";
import { RtcfGrid } from "@/components/RtcfGrid";
import { PromptChallenge } from "@/components/PromptChallenge";
import { PromptChallengeChoice } from "@/components/PromptChallengeChoice";
import { PromptReflection } from "@/components/PromptReflection";
import { BuildScoping } from "@/components/BuildScoping";
import { RecipeEngine } from "@/components/RecipeEngine";
import { PeerReview } from "@/components/PeerReview";
import { SaveWrap } from "@/components/SaveWrap";
import { MentorResources } from "@/components/MentorResources";
import { StackReading } from "@/components/StackReading";
import { FeedbackForm } from "@/components/FeedbackForm";

export const AreaPage = () => {
  const { slug } = useParams();
  const area = getAreaBySlug(slug);
  const { highestStartedOrder, markAreaStarted } = useSession();
  const { setPosition } = useRightNow();
  const [dismissed, setDismissed] = useState(() => isAreaDismissed(area.id));
  const [stepIndex, setStepIndex] = useState(0);
  const [, setBuildTimerStarted] = usePersistentState("build.timerStarted", false);

  const steps = config.content?.[area.id]?.steps || [];
  const segments = config.segments?.[area.id] || [];
  const hasGuide = segments.length > 0;

  // Soft, session-scoped out-of-order gate. Always-available areas are exempt.
  const outOfOrder =
    !area.always_available && area.order > highestStartedOrder && !dismissed;

  // Feed the Right Now bar. Clear it while the interstitial is showing.
  useEffect(() => {
    if (outOfOrder) setPosition({ areaId: null, stepIndex: 0 });
    else setPosition({ areaId: area.id, stepIndex });
  }, [area.id, stepIndex, outOfOrder, setPosition]);

  if (outOfOrder) {
    return (
      <section data-testid={`interstitial-${slug}`} className="max-w-xl">
        <p className="mono-label text-ink/60 mb-3">{area.label}</p>
        <h1 className="font-display text-4xl sm:text-5xl leading-none mb-4">
          This one comes later.
        </h1>
        <p className="text-ink/70 text-lg mb-8">
          This comes after {area.comes_after}.
        </p>
        <button
          type="button"
          data-testid={`open-anyway-${slug}`}
          onClick={() => {
            dismissArea(area.id);
            setDismissed(true);
          }}
          className="underline underline-offset-4 text-ink/60 hover:text-orange-dark transition-colors duration-150"
        >
          Open it anyway
        </button>
      </section>
    );
  }

  const step = steps[stepIndex];
  const navBtn =
    "text-sm underline underline-offset-4 transition-colors duration-150 disabled:no-underline disabled:text-ink/30 hover:text-orange-dark";

  // Timers carry their handlers here so the segment guide can render them.
  const timers = (area.timers || []).map((timer) => ({
    ...timer,
    onStart: () => {
      markAreaStarted(area.order);
      if (area.id === "build" && timer.label === "Building")
        setBuildTimerStarted(true);
    },
  }));

  const currentStepMinutes = segments
    .filter((s) => s.step === stepIndex)
    .reduce((sum, s) => sum + s.minutes, 0);

  const stepper = steps.length > 1 && (
    <div data-testid="area-stepper" className="mb-6 flex items-center justify-between">
      <button
        data-testid="step-prev"
        className={navBtn}
        disabled={stepIndex === 0}
        onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
      >
        &larr; Previous
      </button>
      <span className="mono-label text-ink/60 text-center px-4">
        Step {stepIndex + 1} of {steps.length}
        {step?.title ? ` · ${step.title}` : ""}
      </span>
      <button
        data-testid="step-next"
        className={navBtn}
        disabled={stepIndex === steps.length - 1}
        onClick={() => setStepIndex((i) => Math.min(steps.length - 1, i + 1))}
      >
        Next &rarr;
      </button>
    </div>
  );

  const body =
    area.id === "mentors" ? (
      <MentorResources />
    ) : area.id === "prompt_school" && stepIndex === 0 ? (
      <RtcfGrid />
    ) : area.id === "prompt_school" && stepIndex === 1 ? (
      <PromptChallenge />
    ) : area.id === "prompt_school" && stepIndex === 2 ? (
      <PromptChallengeChoice />
    ) : area.id === "prompt_school" && stepIndex === 3 ? (
      <PromptReflection />
    ) : area.id === "build" && stepIndex === 0 ? (
      <BuildScoping />
    ) : area.id === "build" && stepIndex === 1 ? (
      <RecipeEngine />
    ) : area.id === "build" && stepIndex === 2 ? (
      <PeerReview />
    ) : area.id === "build" && stepIndex === 3 ? (
      <SaveWrap />
    ) : area.id === "stack" ? (
      <StackReading />
    ) : area.id === "feedback" ? (
      <FeedbackForm />
    ) : (
      <div className="bg-white border border-sand p-8">
        <p className="text-ink/60">{config.ui.area_coming_soon}</p>
      </div>
    );

  return (
    <section data-testid={`area-${slug}`}>
      {area.id !== "stack" && area.id !== "feedback" && (
        <>
          <p className="mono-label text-ink/60 mb-3">Section</p>
          <h1 className="font-display text-4xl sm:text-5xl leading-none mb-3">
            {area.label}
          </h1>
        </>
      )}

      {hasGuide ? (
        <div className="lg:grid lg:grid-cols-[1fr_300px] lg:gap-10 lg:items-start mt-6">
          {/* Guide: first in DOM (top on mobile, under Right Now bar), right rail on desktop */}
          <div className="lg:col-start-2 lg:row-start-1 mb-6 lg:mb-0 lg:sticky lg:top-6 min-w-0">
            <SegmentGuide
              area={area}
              timers={timers}
              segments={segments}
              stepIndex={stepIndex}
              onSelectStep={setStepIndex}
            />
          </div>
          <div className="lg:col-start-1 lg:row-start-1 min-w-0">
            {stepper}
            {currentStepMinutes > 0 && (
              <div className="mb-6 -mt-2">
                <StepEstimate minutes={currentStepMinutes} />
              </div>
            )}
            {body}
          </div>
        </div>
      ) : (
        <div className="mt-6">
          {stepper}
          {body}
        </div>
      )}
    </section>
  );
};
